import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Search, ShieldCheck, ShieldAlert, Filter, X, Archive, Download } from "lucide-react";
import { toast } from "sonner";

const ACTION_COLOR = {
    "admin.login.success": "text-emerald-400",
    "admin.login.failure": "text-red-400",
    "admin.logout": "text-white/70",
    "admin.user.create": "text-[#7C3AED]",
    "admin.user.update": "text-blue-400",
    "admin.user.suspend": "text-amber-400",
    "admin.user.reactivate": "text-emerald-400",
    "admin.user.delete": "text-red-400",
    "admin.user.password_reset": "text-amber-400",
    "admin.password.change": "text-blue-400",
};

export default function AuditPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [actorEmail, setActorEmail] = useState("");
    const [action, setAction] = useState("");
    const [resourceType, setResourceType] = useState("");
    const [success, setSuccess] = useState("");
    const [chain, setChain] = useState(null);
    const [detail, setDetail] = useState(null);
    const [archives, setArchives] = useState([]);
    const [archiving, setArchiving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (actorEmail) params.set("actor_email", actorEmail);
            if (action) params.set("action", action);
            if (resourceType) params.set("resource_type", resourceType);
            if (success) params.set("success", success);
            params.set("limit", "200");
            const { data } = await api.get(`/admin/audit?${params.toString()}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    const verifyChain = async () => {
        try {
            const { data } = await api.get("/admin/audit/verify");
            setChain(data);
            toast[data.ok ? "success" : "error"](
                data.ok ? `Chain verified · ${data.checked} row(s)` : `Chain BROKEN at seq ${data.first_bad_sequence}: ${data.reason}`
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Verify failed");
        }
    };

    useEffect(() => { load(); verifyChain(); loadArchives(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadArchives = async () => {
        try {
            const { data } = await api.get("/admin/audit/archives");
            setArchives(data.items || []);
        } catch (e) {
            // not critical
        }
    };

    const createArchive = async () => {
        setArchiving(true);
        try {
            const { data } = await api.post("/admin/audit/archive");
            if (data.archived === 0) {
                toast.info("Nothing new to archive — all rows are already archived.");
            } else {
                toast.success(`Archived ${data.archived} audit row(s) · sha256=${data.sha256?.slice(0, 12)}…`);
            }
            loadArchives();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Archive failed");
        } finally {
            setArchiving(false);
        }
    };

    const verifyArchive = async (archiveId) => {
        try {
            const { data } = await api.get(`/admin/audit/archives/${archiveId}/verify`);
            toast[data.ok ? "success" : "error"](
                data.ok ? `Archive intact · sha256 matches` : `Archive TAMPERED: ${data.reason || "hash mismatch"}`
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Verify failed");
        }
    };

    const downloadArchive = (archiveId, fname) => {
        const token = localStorage.getItem("tf_token");
        fetch(`${API}/admin/audit/archives/${archiveId}/download`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: "include",
        })
            .then((r) => r.ok ? r.blob() : Promise.reject(new Error("Download failed")))
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = fname;
                a.click();
                URL.revokeObjectURL(url);
            })
            .catch((e) => toast.error(e.message));
    };

    return (
        <div className="px-8 py-8">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED]">Compliance</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Audit Trail</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        ALCOA++ compliant, 21 CFR Part 11 ready. Every admin action is
                        recorded here with cryptographic hash-chain tamper evidence.
                        This log is append-only — there is no delete endpoint.
                    </p>
                </div>
                <button
                    onClick={createArchive}
                    disabled={archiving}
                    data-testid="audit-archive-create"
                    className="px-3 py-2 border border-[#7C3AED]/40 text-[#7C3AED] rounded-md text-xs font-medium hover:bg-[#7C3AED]/10 disabled:opacity-40 flex items-center gap-1.5"
                >
                    <Archive className={`w-4 h-4 ${archiving ? "animate-pulse" : ""}`} strokeWidth={1.8} />
                    {archiving ? "Archiving…" : "Archive now"}
                </button>
                <button
                    onClick={verifyChain}
                    data-testid="audit-verify"
                    className={`px-3 py-2 border rounded-md text-xs font-medium flex items-center gap-1.5 ${
                        chain?.ok === false
                            ? "border-red-400/40 text-red-400 hover:bg-red-400/10"
                            : "border-emerald-400/40 text-emerald-400 hover:bg-emerald-400/10"
                    }`}
                >
                    {chain?.ok === false ? <ShieldAlert className="w-4 h-4" strokeWidth={1.8} /> : <ShieldCheck className="w-4 h-4" strokeWidth={1.8} />}
                    {chain == null ? "Verify chain" : chain.ok ? `Chain OK (${chain.checked} rows)` : `CHAIN BROKEN at #${chain.first_bad_sequence}`}
                </button>
            </div>

            {/* Filters */}
            <form onSubmit={(e) => { e.preventDefault(); load(); }}
                data-testid="audit-filters"
                className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-4 flex items-end gap-3 flex-wrap">
                <F label="Actor email" value={actorEmail} on={setActorEmail} testId="audit-actor" />
                <F label="Action contains" value={action} on={setAction} testId="audit-action" />
                <F label="Resource type" value={resourceType} on={setResourceType} testId="audit-resource-type" />
                <div>
                    <div className="label-xs mb-1.5 flex items-center gap-1.5"><Filter className="w-3 h-3" /> Success</div>
                    <select value={success} onChange={(e) => setSuccess(e.target.value)} data-testid="audit-success"
                        className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                        <option value="">Any</option>
                        <option value="true">Only success</option>
                        <option value="false">Only failures</option>
                    </select>
                </div>
                <button type="submit" className="px-4 py-2 bg-[#7C3AED] text-white rounded-md text-sm font-medium hover:bg-[#7C3AED]/90">
                    Apply
                </button>
                <div className="ml-auto text-[11px] font-mono text-white/40">
                    {total} event(s)
                </div>
            </form>

            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="text-left">
                            <th className="label-xs px-5 py-3 w-[60px]">Seq</th>
                            <th className="label-xs px-5 py-3">Time</th>
                            <th className="label-xs px-5 py-3">Actor</th>
                            <th className="label-xs px-5 py-3">Action</th>
                            <th className="label-xs px-5 py-3">Resource</th>
                            <th className="label-xs px-5 py-3">IP</th>
                            <th className="label-xs px-5 py-3 text-right w-[60px]"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="px-5 py-8 text-center text-xs text-white/40 font-mono">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={7} className="px-5 py-16 text-center text-xs text-white/40 font-mono">No events match the filter.</td></tr>
                        ) : items.map((r) => (
                            <tr key={r.id} onClick={() => setDetail(r)} data-testid={`audit-row-${r.sequence_no}`}
                                className="data-row border-t border-white/5 cursor-pointer hover:bg-white/[0.02]">
                                <td className="px-5 py-3 font-mono text-[11px] text-white/40">#{r.sequence_no}</td>
                                <td className="px-5 py-3 font-mono text-[11px] text-white/70">{new Date(r.timestamp_utc).toLocaleString()}</td>
                                <td className="px-5 py-3 text-xs">
                                    <div className="font-mono">{r.actor_email || <span className="text-white/30">—</span>}</div>
                                    <div className="text-[10px] text-white/40">{r.actor_role}{r.actor_kind !== "admin" ? ` · ${r.actor_kind}` : ""}</div>
                                </td>
                                <td className="px-5 py-3">
                                    <span className={`font-mono text-xs ${ACTION_COLOR[r.action] || "text-white/80"}`}>
                                        {r.action}
                                    </span>
                                    {!r.success && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded-sm bg-red-500/20 text-red-400 font-mono">fail</span>}
                                </td>
                                <td className="px-5 py-3 font-mono text-[11px] text-white/60">
                                    <div>{r.resource_type}</div>
                                    {r.resource_id && <div className="text-[10px] text-white/40 truncate max-w-[180px]">{r.resource_id}</div>}
                                </td>
                                <td className="px-5 py-3 font-mono text-[11px] text-white/50">{r.ip_address || "—"}</td>
                                <td className="px-5 py-3 text-right">
                                    <Search className="w-3 h-3 text-white/30 inline" strokeWidth={1.8} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {archives.length > 0 && (
                <div className="mt-8" data-testid="audit-archives-section">
                    <div className="flex items-center mb-3">
                        <Archive className="w-4 h-4 text-[#7C3AED] mr-2" strokeWidth={1.8} />
                        <h2 className="text-base font-display tracking-tight">Immutable archives</h2>
                        <div className="ml-3 text-[11px] font-mono text-white/40">{archives.length} snapshot(s)</div>
                    </div>
                    <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden">
                        <table className="w-full">
                            <thead>
                                <tr className="text-left">
                                    <th className="label-xs px-5 py-3">Created</th>
                                    <th className="label-xs px-5 py-3">Sequence range</th>
                                    <th className="label-xs px-5 py-3">Rows</th>
                                    <th className="label-xs px-5 py-3">Size</th>
                                    <th className="label-xs px-5 py-3">SHA-256</th>
                                    <th className="label-xs px-5 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {archives.map((a) => (
                                    <tr key={a.id} className="data-row border-t border-white/5" data-testid={`archive-row-${a.id}`}>
                                        <td className="px-5 py-3 font-mono text-xs text-white/70">{new Date(a.created_at).toLocaleString()}</td>
                                        <td className="px-5 py-3 font-mono text-xs">#{a.sequence_from}–#{a.sequence_to}</td>
                                        <td className="px-5 py-3 font-mono text-xs text-[#7C3AED]">{a.row_count}</td>
                                        <td className="px-5 py-3 font-mono text-xs text-white/60">{((a.size_bytes || 0) / 1024).toFixed(1)} KB</td>
                                        <td className="px-5 py-3 font-mono text-[10px] text-white/50 truncate max-w-[180px]">{a.sha256}</td>
                                        <td className="px-5 py-3 text-right">
                                            <div className="flex items-center justify-end gap-1.5">
                                                <button onClick={() => verifyArchive(a.id)} className="px-2 py-1 text-[11px] font-mono border border-emerald-400/30 text-emerald-400 rounded hover:bg-emerald-400/10" data-testid={`archive-verify-${a.id}`}>
                                                    verify
                                                </button>
                                                <button onClick={() => downloadArchive(a.id, a.storage_path.split("/").pop())} className="p-1.5 rounded hover:bg-white/10 text-white/70" data-testid={`archive-download-${a.id}`}>
                                                    <Download className="w-3.5 h-3.5" strokeWidth={1.6} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {detail && <DetailModal row={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function F({ label, value, on, testId }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}</div>
            <input value={value} onChange={(e) => on(e.target.value)} data-testid={testId}
                className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono min-w-[180px]" />
        </div>
    );
}

function DetailModal({ row, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className="w-full max-w-2xl max-h-[80vh] overflow-y-auto bg-[#0a0a0a] border border-white/15 rounded-md" onClick={(e) => e.stopPropagation()} data-testid="audit-detail-modal">
                <div className="px-5 py-4 border-b border-white/10 flex items-center sticky top-0 bg-[#0a0a0a] z-10">
                    <div className="font-mono text-xs text-white/40">#{row.sequence_no}</div>
                    <div className="ml-3 font-display text-lg tracking-tight">{row.action}</div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3 font-mono text-xs">
                    <KV k="time" v={row.timestamp_utc} />
                    <KV k="actor" v={`${row.actor_email || "—"} (${row.actor_role || "?"}, ${row.actor_kind})`} />
                    <KV k="resource" v={`${row.resource_type}${row.resource_id ? " · " + row.resource_id : ""}`} />
                    <KV k="success" v={row.success ? "yes" : "no"} />
                    {row.error_message && <KV k="error" v={row.error_message} warn />}
                    {row.reason && <KV k="reason" v={row.reason} highlight />}
                    <KV k="ip" v={row.ip_address || "—"} />
                    <KV k="user_agent" v={row.user_agent || "—"} truncate />
                    {row.request_id && <KV k="request_id" v={row.request_id} />}
                    {row.before_snapshot && (
                        <div>
                            <div className="label-xs mt-3 mb-1">Before</div>
                            <pre className="bg-[#050505] border border-white/10 rounded-md p-3 text-[11px] whitespace-pre-wrap break-words">{JSON.stringify(row.before_snapshot, null, 2)}</pre>
                        </div>
                    )}
                    {row.after_snapshot && (
                        <div>
                            <div className="label-xs mt-3 mb-1">After</div>
                            <pre className="bg-[#050505] border border-white/10 rounded-md p-3 text-[11px] whitespace-pre-wrap break-words">{JSON.stringify(row.after_snapshot, null, 2)}</pre>
                        </div>
                    )}
                    <div>
                        <div className="label-xs mt-3 mb-1">Hash chain</div>
                        <div className="bg-[#050505] border border-white/10 rounded-md p-3 text-[10px] space-y-1">
                            <div><span className="text-white/40">prev_hash:</span> <span className="break-all">{row.prev_hash}</span></div>
                            <div><span className="text-white/40">hash:</span> <span className="break-all text-[#7C3AED]">{row.hash}</span></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function KV({ k, v, warn, highlight, truncate }) {
    return (
        <div className="flex gap-3">
            <div className="text-white/40 w-[90px] flex-shrink-0">{k}</div>
            <div className={`flex-1 ${warn ? "text-red-400" : highlight ? "text-amber-400" : "text-white/80"} ${truncate ? "truncate" : "break-words"}`}>
                {v}
            </div>
        </div>
    );
}
